<script module>
    export { default as layout } from '../../layouts/AdminLayout.svelte';
</script>

<script lang="ts">
    import ActivityFeed, { type ActivityItem } from '../../components/ActivityFeed.svelte';
    import { Link, useForm, page } from '@inertiajs/svelte';
    import { formatDate } from '../../utils/dates';
    import { users } from '@/routes/admin';
    import { update } from '@/routes/admin/users';

    interface User {
        id: number;
        name: string;
        email: string;
        email_reminders: boolean;
        leaderboard_opt_in: boolean;
        roles: string[];
        created_at: string;
    }

    interface AvailableRole {
        value: string;
        label: string;
        description: string;
        color: string;
        icon: string;
    }

    const colorClasses: Record<string, { checkbox: string; icon: string }> = {
        purple: { checkbox: 'text-purple-600 focus:ring-purple-500', icon: 'text-purple-400' },
        blue: { checkbox: 'text-cyan-600 focus:ring-cyan-500', icon: 'text-cyan-400' },
        green: { checkbox: 'text-green-600 focus:ring-green-500', icon: 'text-green-400' },
        gray: { checkbox: 'text-gray-600 focus:ring-gray-500', icon: 'text-gray-400' },
    };

    let { user, availableRoles, activity }: { user: User; availableRoles: AvailableRole[]; activity: ActivityItem[] } = $props();

    const isSelf = $derived(($page as any).props.auth?.user?.id === user.id);

    const form = useForm({
        name: user.name,
        email_reminders: user.email_reminders,
        leaderboard_opt_in: user.leaderboard_opt_in,
        roles: [...user.roles],
    });

    function toggleRole(role: string) {
        if ($form.roles.includes(role)) {
            $form.roles = $form.roles.filter((r: string) => r !== role);
        } else {
            $form.roles = [...$form.roles, role];
        }
    }

    function submit(e: Event) {
        e.preventDefault();
        $form.patch(update.url(user.id), { invalidateCacheTags: ['admin-users'] });
    }
</script>

<svelte:head>
    <title>Edit {user.name} — Admin — Case of the Day</title>
</svelte:head>

<div class="mb-6">
    <Link href={users()} class="text-sm text-cyan-400 hover:text-cyan-300">&larr; Back to Users</Link>
</div>

    <h1 class="text-2xl font-bold text-gray-100 mb-6">Edit User</h1>

    <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
        <form onsubmit={submit} class="space-y-6">
            <div class="bg-gray-900 rounded-lg shadow border border-gray-700 p-6 space-y-4">
                <div>
                    <label for="name" class="block text-sm font-medium text-gray-300 mb-1">Name</label>
                    <input
                        id="name"
                        type="text"
                        bind:value={$form.name}
                        class="block w-full rounded-md border border-gray-700 bg-gray-800 text-gray-100 px-3 py-2 text-sm shadow-sm focus:border-cyan-500 focus:ring-cyan-500"
                    />
                    {#if $form.errors.name}<p class="text-red-500 text-sm mt-1">{$form.errors.name}</p>{/if}
                </div>

                <div>
                    <label for="email" class="block text-sm font-medium text-gray-300 mb-1">Email</label>
                    <input
                        id="email"
                        type="email"
                        value={user.email}
                        disabled
                        class="block w-full rounded-md border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-500"
                    />
                    <p class="text-xs text-gray-400 mt-1">Email cannot be changed from this page.</p>
                </div>

                <label for="email_reminders" class="flex items-center gap-3 cursor-pointer">
                    <input
                        id="email_reminders"
                        type="checkbox"
                        bind:checked={$form.email_reminders}
                        class="h-4 w-4 rounded border-gray-700 text-cyan-600 focus:ring-cyan-500"
                    />
                    <div>
                        <span class="text-sm font-medium text-gray-300">Email reminders</span>
                        <p class="text-xs text-gray-500">User receives email reminders about new cases.</p>
                    </div>
                </label>

                <label for="leaderboard_opt_in" class="flex items-center gap-3 cursor-pointer">
                    <input
                        id="leaderboard_opt_in"
                        type="checkbox"
                        bind:checked={$form.leaderboard_opt_in}
                        class="h-4 w-4 rounded border-gray-700 text-cyan-600 focus:ring-cyan-500"
                    />
                    <div>
                        <span class="text-sm font-medium text-gray-300">Leaderboard opt-in</span>
                        <p class="text-xs text-gray-500">User appears on public leaderboards.</p>
                    </div>
                </label>
            </div>

            <div class="bg-gray-900 rounded-lg shadow border border-gray-700 p-6">
                <h2 class="text-sm font-medium text-gray-300 mb-3">Roles</h2>
                <div class="space-y-3">
                    {#each availableRoles as role (role.value)}
                        {@const isLockedAdmin = isSelf && role.value === 'admin'}
                        <label for="role_{role.value}" class="flex items-start gap-3 {isLockedAdmin ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}">
                            <input
                                id="role_{role.value}"
                                type="checkbox"
                                checked={$form.roles.includes(role.value)}
                                onchange={() => toggleRole(role.value)}
                                disabled={isLockedAdmin}
                                class="h-4 w-4 mt-0.5 rounded border-gray-700 {colorClasses[role.color].checkbox}"
                            />
                            <div class="flex items-start gap-2">
                                <!-- eslint-disable-next-line svelte/no-at-html-tags -->
                                <span class="size-4 mt-0.5 {colorClasses[role.color].icon}">{@html role.icon}</span>
                                <div>
                                    <span class="text-sm font-medium text-gray-300">{role.label}</span>
                                    <p class="text-xs text-gray-500">{role.description}</p>
                                    {#if isLockedAdmin}
                                        <p class="text-xs text-amber-400/80 mt-0.5">You cannot remove admin from your own account.</p>
                                    {/if}
                                </div>
                            </div>
                        </label>
                    {/each}
                </div>
                {#if $form.errors.roles}<p class="text-red-500 text-sm mt-1">{$form.errors.roles}</p>{/if}
            </div>

            <div class="bg-gray-900 rounded-lg shadow border border-gray-700 p-6">
                <h2 class="text-sm font-medium text-gray-300 mb-2">Account Info</h2>
                <dl class="text-sm text-gray-500 space-y-1">
                    <div class="flex gap-2">
                        <dt class="font-medium text-gray-400">Joined:</dt>
                        <dd>{formatDate(user.created_at)}</dd>
                    </div>
                </dl>
            </div>

            <div class="flex items-center gap-3">
                <button
                    type="submit"
                    disabled={$form.processing || !$form.name.trim()}
                    class="bg-cyan-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-cyan-500 disabled:opacity-50 transition-colors"
                >
                    {$form.processing ? 'Saving...' : 'Save Changes'}
                </button>
                {#if $form.recentlySuccessful}
                    <span class="text-sm text-green-400">Saved.</span>
                {/if}
                <Link href={users()} class="text-sm text-gray-500 hover:text-gray-300">Cancel</Link>
            </div>
        </form>

        <div>
            <h2 class="text-lg font-semibold text-gray-100 mb-5">Activity</h2>
            <ActivityFeed {activity} emptyMessage="No activity for this user." />
        </div>
    </div>
